import BAAS from '@coderz/baas';

import {
  REACT_APP_RSD_DOMAIN,
  REACT_APP_AUTH_CUBES_JWT_KEY,
  REACT_APP_AUTH_CUBES_JWT_ISSUER,
  REACT_APP_AUTH_CUBES_JWT_AUDIENCE
} from '../api/base';

// eslint-disable-next-line import/no-cycle
import JWTGenerator from './jwtGenerator';


let baasInstance = null;
let currentToken = null;
let jwtGenerator = null;

const getJWTGenerator = () => {
  if (!jwtGenerator) {
    jwtGenerator = new JWTGenerator(
      REACT_APP_AUTH_CUBES_JWT_KEY,
      REACT_APP_AUTH_CUBES_JWT_ISSUER,
      REACT_APP_AUTH_CUBES_JWT_AUDIENCE
    );
  }

  return jwtGenerator;
};

const getErrorMessage = error => {
  if (!error) {
    return 'Unknown error occurred';
  }

  if (error.error && typeof error.error === 'string' && error.code) {
    return error.error;
  }
  if (error.success === false && error.error) {
    return typeof error.error === 'string' ? error.error : String(error.error);
  }

  if (typeof error === 'string') {
    return error;
  }
  if (error.message) {
    return error.message;
  }
  if (error.error) {
    return typeof error.error === 'string' ? error.error : String(error.error);
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.response?.data?.error) {
    return String(error.response.data.error);
  }
  try {
    return JSON.stringify(error);
  } catch (e) {
    return String(error);
  }
};

/**
 * Get or create BAAS instance with dynamically generated JWT token
 * @returns {Promise<BAAS>} - BAAS instance
 */
const getBAAS = async() => {
  try {
    const token = await getJWTGenerator().generateJwtToken();

    if (!token) {
      throw new Error('JWT token generation returned empty token');
    }

    if (baasInstance && currentToken === token) {
      return baasInstance;
    }

    const baasUrl = REACT_APP_RSD_DOMAIN
      ? REACT_APP_RSD_DOMAIN.replace(/\/$/, '')
      : 'https://coderz.devclan.io';

    baasInstance = new BAAS({
      url: baasUrl,
      token
    });

    currentToken = token;
    baasInstance._token = token;

    return baasInstance;
  } catch (error) {
    const fallbackToken = process.env.REACT_APP_BAAS_TOKEN;
    if (fallbackToken) {
      if (baasInstance && currentToken === fallbackToken) {
        return baasInstance;
      }

      const baasUrl = REACT_APP_RSD_DOMAIN
        ? REACT_APP_RSD_DOMAIN.replace(/\/$/, '')
        : 'https://coderz.devclan.io';

      baasInstance = new BAAS({
        url: baasUrl,
        token: fallbackToken
      });

      currentToken = fallbackToken;
      baasInstance._token = fallbackToken;

      return baasInstance;
    }

    throw new Error(
      `Failed to generate JWT token: ${error.message}. ` +
      'No fallback token available.'
    );
  }
};

/**
 * Call Coderz agent
 * @param {string} agentName - Agent name (e.g., 'sanad')
 * @param {string} message - User message
 * @param {string} sessionId - Optional session ID to continue existing session
 * @param {object} outputSchema - Optional output schema for structured response
 * @returns {Promise<{content: string, sessionId: string}>}
 */
export const callAgent = async(
  agentName,
  message,
  sessionId = null,
  outputSchema = null
) => {
  try {
    const baas = await getBAAS();

    if (!agentName || typeof agentName !== 'string') {
      throw new Error('Agent name must be a non-empty string');
    }

    let session;
    if (sessionId) {
      try {
        session = await baas.agent('sanad').session(sessionId);
      } catch (error) {
        session = await baas.agent('sanad').newSession();
      }
    } else {
      session = await baas.agent('sanad').newSession();
    }

    const options = outputSchema ? { outputSchema } : {};
    const result = await session.prompt(message, options);

    const finalSessionId = session.id || session.sessionId;

    return {
      content: result.content,
      sessionId: finalSessionId
    };
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    const errorCode = error?.code || error?.response?.status || error?.status;

    if (
      errorCode === 401
      || errorMessage.includes('401')
      || errorMessage.includes('UNAUTHORIZED')
    ) {
      const backendError = new Error('BACKEND_NOT_CONFIGURED');
      backendError.message
        = 'The backend is not configured to proxy agent requests to Coderz. '
        + 'Please contact the backend team to configure the /agent/* endpoint.';
      backendError.code = 401;
      throw backendError;
    }

    if (
      errorCode === 403
      || error?.code === 403
      || (error?.success === false && error?.code === 403)
      || errorMessage.includes('403')
      || errorMessage.includes('FORBIDDEN')
      || error?.error === 'FORBIDDEN'
    ) {
      const forbiddenError = new Error('FORBIDDEN');
      forbiddenError.message
        = 'Access forbidden (403). Please check your JWT token generation '
        + 'or contact support.';
      forbiddenError.code = error?.code || 403;
      forbiddenError.success = false;
      forbiddenError.error = 'FORBIDDEN';
      forbiddenError.originalError = error;
      throw forbiddenError;
    }

    const formattedError = new Error(errorMessage);
    formattedError.originalError = error;
    formattedError.code = errorCode;
    formattedError.response = error?.response;
    throw formattedError;
  }
};

/**
 * Create a new agent session
 * @param {string} agentName - Agent name (e.g., 'sanad')
 * @returns {Promise<{sessionId: string}>}
 */
export const createAgentSession = async agentName => {
  try {
    const baas = await getBAAS();

    if (!agentName || typeof agentName !== 'string') {
      throw new Error('Agent name must be a non-empty string');
    }

    const session = await baas.agent('sanad').newSession();

    const sessionId = session.id || session.sessionId;
    if (!sessionId) {
      throw new Error('Session ID not returned from agent');
    }

    return {
      sessionId
    };
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    const errorCode = error?.code || error?.response?.status || error?.status;

    if (
      errorCode === 401
      || errorMessage.includes('401')
      || errorMessage.includes('UNAUTHORIZED')
    ) {
      const backendError = new Error('BACKEND_NOT_CONFIGURED');
      backendError.message
        = 'The backend is not configured to proxy agent requests to Coderz. '
        + 'Please contact the backend team to configure the /agent/* endpoint.';
      backendError.code = 401;
      throw backendError;
    }

    if (
      errorCode === 403
      || error?.code === 403
      || (error?.success === false && error?.code === 403)
      || errorMessage.includes('403')
      || errorMessage.includes('FORBIDDEN')
      || error?.error === 'FORBIDDEN'
    ) {
      const forbiddenError = new Error('FORBIDDEN');
      forbiddenError.message
        = 'Access forbidden (403). Please check your JWT token generation '
        + 'or contact support.';
      forbiddenError.code = error?.code || 403;
      forbiddenError.success = false;
      forbiddenError.error = 'FORBIDDEN';
      forbiddenError.originalError = error;
      throw forbiddenError;
    }

    const formattedError = new Error(errorMessage);
    formattedError.originalError = error;
    formattedError.code = errorCode;
    formattedError.response = error?.response;
    throw formattedError;
  }
};

